import type { LineRange } from '@/lib/pr-v2/code-changes/repo-file-resolution';
import type { PrV2RepoFileSide } from '@/lib/pr-v2/code-changes/types';
import type { AnnotationSide } from '@pierre/diffs';

/** The diff file a pointer event landed in. */
export type PrV2RepoFileLineSource = {
  filename: string;
  previousFilename: string | null;
  /**
   * The side a full-file code view shows, or null while the file renders as a diff. Pierre types
   * every code-view line `context`, so only the view knows which revision the lines come from.
   */
  codeViewSide: AnnotationSide | null;
};

/** A code line under a pointer, with what its specifiers resolve against. */
export type PrV2RepoFileLineHit = {
  line: Element;
  text: string;
  /** The character under the pointer; past the end of the text, the text's length. */
  offset: number;
  side: PrV2RepoFileSide;
  fromPath: string;
};

/** A text node of a line and the characters `[start, end)` of the line's text it holds. */
type LineText = { node: Node; start: number; end: number };

/** Right-to-left letters and bidi controls lay characters out of index order. */
const BIDI_REORDERING =
  /[\u0590-\u08FF\u200F\u202A-\u202E\u2066-\u2069\uFB1D-\uFDFF\uFE70-\uFEFF\u{10800}-\u{10FFF}\u{1E800}-\u{1EFFF}]/u;

/**
 * The code line a pointer event on a Pierre diff landed on, or null off a code line. Pierre
 * renders into an open shadow root, so the target comes from the composed path. As in Pierre's own
 * pointer handling, `data-line` marks a code line, whose text is the code alone: line numbers sit
 * in separate gutter cells.
 */
export function findPrV2RepoFileLineHit(
  event: MouseEvent,
  source: PrV2RepoFileLineSource,
): PrV2RepoFileLineHit | null {
  const target = event.composedPath()[0];

  if (!(target instanceof Node)) {
    return null;
  }

  const line = (target instanceof Element ? target : target.parentElement)?.closest('[data-line]');

  if (!line) {
    return null;
  }

  const texts = indexLineText(line);
  const side = readLineSide(line, source.codeViewSide);

  return {
    line,
    text: line.textContent ?? '',
    offset: measurePointerOffset(
      texts,
      target === line ? null : target,
      event.clientX,
      event.clientY,
    ),
    side,
    fromPath: side === 'base' ? (source.previousFilename ?? source.filename) : source.filename,
  };
}

/** A DOM range over the characters `[start, end)` of a code line's text. */
export function createPrV2RepoFileLineRange(
  line: Element,
  { start, end }: LineRange,
): Range | null {
  const texts = indexLineText(line);
  const first = texts.find((text) => start < text.end);
  const last = texts.find((text) => end <= text.end);

  if (!first || !last) {
    return null;
  }

  const range = line.ownerDocument.createRange();

  range.setStart(first.node, start - first.start);
  range.setEnd(last.node, end - last.start);

  return range;
}

function readLineSide(line: Element, codeViewSide: AnnotationSide | null): PrV2RepoFileSide {
  if (codeViewSide !== null) {
    return codeViewSide === 'deletions' ? 'base' : 'head';
  }

  return line.getAttribute('data-line-type') === 'change-deletion' ? 'base' : 'head';
}

function indexLineText(line: Element): LineText[] {
  const walker = line.ownerDocument.createTreeWalker(line, NodeFilter.SHOW_TEXT);
  const texts: LineText[] = [];
  let start = 0;

  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const end = start + (node.textContent?.length ?? 0);

    texts.push({ node, start, end });
    start = end;
  }

  return texts;
}

/**
 * The character under the pointer in the token it is on, the token's start when nothing is laid
 * out, or the end of the text when the pointer is past it, on the line itself.
 */
function measurePointerOffset(
  texts: readonly LineText[],
  token: Node | null,
  clientX: number,
  clientY: number,
): number {
  const text = token && texts.find(({ node }) => token.contains(node));

  if (!text) {
    return texts.at(-1)?.end ?? 0;
  }

  return text.start + (findCharacterAtPoint(text.node, clientX, clientY) ?? 0);
}

/**
 * Index of the character in a text node whose box holds the point, or null when none does. A
 * binary search measures a handful of boxes for left-to-right text. Right-to-left letters and bidi
 * controls move boxes out of index order, so a miss on text holding them measures every box.
 */
function findCharacterAtPoint(text: Node, clientX: number, clientY: number): number | null {
  const range = text.ownerDocument?.createRange();

  if (!range) {
    return null;
  }

  const index = searchCharacterAtPoint(range, text, clientX, clientY);

  return (
    index ??
    (BIDI_REORDERING.test(text.textContent ?? '')
      ? scanCharacterAtPoint(range, text, clientX, clientY)
      : null)
  );
}

function measureCharacter(range: Range, text: Node, index: number): DOMRect | undefined {
  range.setStart(text, index);
  range.setEnd(text, index + 1);

  return range.getClientRects()[0];
}

function searchCharacterAtPoint(
  range: Range,
  text: Node,
  clientX: number,
  clientY: number,
): number | null {
  let low = 0;
  let high = (text.textContent?.length ?? 0) - 1;

  while (low <= high) {
    const index = Math.floor((low + high) / 2);
    const rect = measureCharacter(range, text, index);

    if (!rect) {
      return null;
    }

    if (clientY < rect.top || (clientY <= rect.bottom && clientX < rect.left)) {
      high = index - 1;
    } else if (clientY > rect.bottom || clientX > rect.right) {
      low = index + 1;
    } else {
      return index;
    }
  }

  return null;
}

function scanCharacterAtPoint(
  range: Range,
  text: Node,
  clientX: number,
  clientY: number,
): number | null {
  const length = text.textContent?.length ?? 0;

  for (let index = 0; index < length; index += 1) {
    const rect = measureCharacter(range, text, index);

    if (!rect) {
      continue;
    }

    if (
      rect.left <= clientX &&
      clientX <= rect.right &&
      rect.top <= clientY &&
      clientY <= rect.bottom
    ) {
      return index;
    }
  }

  return null;
}
